import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Disc3 } from "lucide-react";
import { api, resolveMedia } from "../lib/api";
import { useSEO } from "../lib/useSEO";
import { Reveal } from "../components/Reveal";
import { SectionLabel } from "../components/SectionLabel";

const STREAMING_LABELS = { spotify: "Spotify", apple: "Apple Music", youtube: "YouTube", tidal: "Tidal", amazon: "Amazon Music" };

export default function OfficialMusic() {
  const [items, setItems] = useState(null);

  useSEO("Official Music", "The official recording catalog of Queen Denae — commercial releases, statements, and what's coming next.");

  useEffect(() => {
    api.get("/official").then((r) => setItems(r.data)).catch(() => setItems([]));
  }, []);

  const upcoming = (items || []).filter((i) => i.status === "upcoming");
  const released = (items || []).filter((i) => i.status === "released");

  return (
    <div data-testid="official-music-discography" className="pt-32 sm:pt-40 pb-20">
      <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-12">
        <SectionLabel>Official Music</SectionLabel>
        <Reveal>
          <h1 className="font-serif text-5xl sm:text-7xl tracking-tight text-cream" data-testid="official-heading">
            THE OFFICIAL<br /><span className="italic text-goldglow">RECORDS</span>
          </h1>
          <p className="mt-8 text-lg text-sand max-w-xl leading-relaxed">
            The Vault is where I create freely.<br />The official records are where I make my statements.
          </p>
        </Reveal>

        <div className="mt-16">
          <SectionLabel>Upcoming</SectionLabel>
          {upcoming.length === 0 && (
            <p className="text-sand font-serif italic text-xl" data-testid="official-upcoming-empty">Announcements are on their way.</p>
          )}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {upcoming.map((r, i) => (
              <Reveal key={r.id} delay={i * 0.1}>
                <div data-testid={`official-upcoming-${i}`} className="border border-gold/25 bg-gallery p-8 sm:p-12 relative overflow-hidden">
                  <div className="absolute -right-8 -bottom-8 opacity-10">
                    <Disc3 size={180} className="text-gold" />
                  </div>
                  <p className="font-mono text-[10px] tracking-[0.3em] uppercase text-gold/70">Coming Soon</p>
                  <p className="mt-4 font-serif text-3xl sm:text-5xl tracking-tight text-cream">{r.title}</p>
                  {r.release_date && <p className="mt-3 font-mono text-[11px] tracking-[0.25em] uppercase text-goldglow">{r.release_date}</p>}
                  {r.description && <p className="mt-4 text-sand text-sm leading-relaxed max-w-md">{r.description}</p>}
                  <Link to="/royal-list" data-testid="official-notify-btn" className="mt-7 inline-flex h-12 px-7 items-center gap-2 border border-gold/50 text-gold font-mono text-[10px] tracking-[0.3em] uppercase hover:bg-gold hover:text-obsidian transition-colors">
                    Hear It First <ArrowRight size={13} />
                  </Link>
                </div>
              </Reveal>
            ))}
          </div>
        </div>

        <div className="mt-20">
          <SectionLabel>The Catalog</SectionLabel>
          {released.length === 0 ? (
            <Reveal>
              <div className="border border-gold/15 bg-gallery/50 p-10 sm:p-16 text-center" data-testid="official-catalog-empty">
                <p className="font-serif text-3xl sm:text-4xl text-cream tracking-tight">NEW MUSIC COMING SOON</p>
                <p className="mt-4 text-sand text-sm max-w-md mx-auto leading-relaxed">
                  The next official record is on the way. Until then, the Royal Vault is open — rare recordings from the Queen's private archive, preserved for those who come directly to the source.
                </p>
                <Link to="/vault" data-testid="official-to-vault-btn" className="mt-8 inline-flex h-13 px-8 py-4 items-center gap-2 bg-gold text-obsidian font-mono text-[11px] tracking-[0.3em] uppercase hover:bg-goldglow transition-colors">
                  Enter the Royal Vault
                </Link>
              </div>
            </Reveal>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
              {released.map((r, i) => (
                <Reveal key={r.id} delay={i * 0.1}>
                  <div data-testid={`official-release-${i}`} className="border border-gold/20 bg-gallery">
                    <div className="aspect-square overflow-hidden">
                      {r.artwork_url ? (
                        <img src={resolveMedia(r.artwork_url)} alt={r.title} className="w-full h-full object-cover" />
                      ) : (
                        <div className="w-full h-full bg-velvet flex items-center justify-center"><Disc3 size={48} className="text-gold/30" /></div>
                      )}
                    </div>
                    <div className="p-6">
                      <p className="font-mono text-[10px] tracking-[0.3em] uppercase text-gold/70">{r.release_date}</p>
                      <h3 className="mt-2 font-serif text-2xl text-cream">{r.title}</h3>
                      {r.description && <p className="mt-2 text-sm text-sand line-clamp-2">{r.description}</p>}
                      {r.streaming_links && Object.keys(r.streaming_links).length > 0 && (
                        <div className="mt-4 flex flex-wrap gap-2">
                          {Object.entries(r.streaming_links).filter(([, v]) => v).map(([k, v]) => (
                            <a key={k} href={v} target="_blank" rel="noopener noreferrer" data-testid={`stream-${k}-${i}`} className="px-4 py-2 border border-gold/30 font-mono text-[9px] tracking-[0.25em] uppercase text-sand hover:text-gold hover:border-gold transition-colors">
                              {STREAMING_LABELS[k] || k}
                            </a>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                </Reveal>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
